import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  ChartBar, GraduationCap, CurrencyDollar, Warning, Buildings,
  MapPin, TrendUp, TrendDown, DownloadSimple, X,
} from "@phosphor-icons/react";

// --- Africa continent outline (approximate polygon in lat/lng) ---
const LNG_MIN = -18, LNG_MAX = 52, LAT_MIN = -35, LAT_MAX = 37;
const W = 640, H = 720;

function project(lat, lng) {
  const x = ((lng - LNG_MIN) / (LNG_MAX - LNG_MIN)) * W;
  const y = ((LAT_MAX - lat) / (LAT_MAX - LAT_MIN)) * H;
  return [x, y];
}

const CONTINENT_LATLNG = [
  [21.5, -17], [14.7, -17.5], [11, -16.5], [8, -13], [5, -8],
  [5.2, -4], [5.7, 0.5], [6, 4], [4.5, 7], [3.5, 9.5], [-0.5, 9],
  [-6, 12], [-16, 12], [-23, 14], [-30, 17], [-34.5, 18.5],
  [-33, 27], [-28, 32.5], [-22, 35], [-11, 40.5], [-1.5, 41.7],
  [3, 46], [11.5, 51], [12, 43], [17, 39], [22, 36.5], [31, 32.5],
  [31, 25], [32, 15], [37, 10], [35.5, 0], [35.5, -6], [30, -10], [27, -13],
];
const CONTINENT_PATH = "M " + CONTINENT_LATLNG.map(([la, ln]) => project(la, ln).map(v => v.toFixed(1)).join(",")).join(" L ") + " Z";

const COUNTRY_META = {
  BF: { name: "Burkina Faso", flag: "🇧🇫", capital: "Ouagadougou", lat: 12.36, lng: -1.53 },
  CI: { name: "Côte d'Ivoire", flag: "🇨🇮", capital: "Abidjan",     lat: 5.36,  lng: -4.02 },
  GH: { name: "Ghana",          flag: "🇬🇭", capital: "Accra",       lat: 5.60,  lng: -0.20 },
  KE: { name: "Kenya",          flag: "🇰🇪", capital: "Nairobi",     lat: -1.29, lng: 36.82 },
  ML: { name: "Mali",           flag: "🇲🇱", capital: "Bamako",      lat: 12.65, lng: -8.00 },
  NE: { name: "Niger",          flag: "🇳🇪", capital: "Niamey",      lat: 13.51, lng:  2.11 },
  SN: { name: "Sénégal",        flag: "🇸🇳", capital: "Dakar",       lat: 14.72, lng: -17.47 },
};

const CURRENCY_LABEL = { XOF: "F CFA", XAF: "FCFA", GHS: "GH₵", KES: "KSh", NGN: "₦", USD: "$", EUR: "€" };
function fmtCurrency(v, code = "XOF") {
  const num = Math.round(v || 0);
  return `${num.toLocaleString("fr-FR")} ${CURRENCY_LABEL[code] || code}`;
}

// Growth pill component
function Growth({ current, previous, isFr }) {
  if (previous == null || previous === 0) {
    if (current > 0) return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-secondary" data-testid="growth-new">
        <TrendUp size={10} weight="fill" /> {isFr ? "Nouveau" : "New"}
      </span>
    );
    return <span className="text-[10px] text-muted-foreground">—</span>;
  }
  const diff = current - previous;
  const pct = (diff / previous) * 100;
  const up = pct >= 0;
  const Icon = up ? TrendUp : TrendDown;
  const cls = up ? "text-secondary" : "text-destructive";
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-semibold ${cls}`} data-testid="growth-pill">
      <Icon size={10} weight="fill" />
      {up ? "+" : ""}{pct.toFixed(0)}%
    </span>
  );
}

export default function Panafrican() {
  const { t, i18n } = useTranslation();
  const isFr = (i18n.language || "fr").startsWith("fr");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverCC, setHoverCC] = useState(null);
  const [filterCC, setFilterCC] = useState(null); // click-to-filter

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/analytics");
        setData(r.data);
      } finally { setLoading(false); }
    })();
  }, []);

  const byCountry = useMemo(() => {
    if (!data) return {};
    const out = {};
    for (const s of data.per_school || []) {
      const cc = (s.country || "??").toUpperCase();
      if (!out[cc]) out[cc] = {
        cc, name: COUNTRY_META[cc]?.name || cc, schools: [],
        students: 0, teachers: 0, classes: 0,
        month_revenue: 0, prev_month_revenue: 0, unpaid_balance: 0, currency: s.currency,
      };
      out[cc].schools.push(s);
      out[cc].students += s.students;
      out[cc].teachers += s.teachers;
      out[cc].classes += s.classes;
      out[cc].month_revenue += s.month_revenue;
      out[cc].prev_month_revenue += (s.prev_month_revenue || 0);
      out[cc].unpaid_balance += s.unpaid_balance;
    }
    return out;
  }, [data]);

  const countryList = useMemo(() =>
    Object.values(byCountry).sort((a, b) => b.students - a.students),
    [byCountry]);

  const maxStudents = Math.max(1, ...countryList.map(c => c.students));

  const filteredSchools = useMemo(() => {
    if (!data?.per_school) return [];
    if (!filterCC) return data.per_school;
    return data.per_school.filter(s => (s.country || "").toUpperCase() === filterCC);
  }, [data, filterCC]);

  const filteredCountries = useMemo(() =>
    filterCC ? countryList.filter(c => c.cc === filterCC) : countryList,
    [countryList, filterCC]);

  if (loading) return <div className="p-8 text-muted-foreground text-sm">{t("common.loading")}</div>;
  if (!data) return <div className="p-8 text-destructive text-sm">Données indisponibles</div>;

  const tot = data.totals || {};
  const monthRev = tot.month_revenue_all || 0;
  const prevMonthRev = tot.prev_month_revenue_all || 0;
  const filterMeta = filterCC ? COUNTRY_META[filterCC] : null;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-testid="panafrican-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-[10px] uppercase tracking-widest font-semibold">
            <ChartBar size={12} weight="fill" /> {t("panafrican.kicker")}
          </div>
          <h1 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
            {t("panafrican.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{t("panafrican.subtitle")}</p>
          {filterCC && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-semibold" data-testid="filter-banner">
              {filterMeta?.flag} {t("panafrican.filter_active", { country: filterMeta?.name || filterCC })}
              <button onClick={() => setFilterCC(null)} className="hover:bg-black/10 rounded-full p-0.5" data-testid="btn-clear-filter" aria-label={t("panafrican.clear_filter")}>
                <X size={12} weight="bold" />
              </button>
            </div>
          )}
        </div>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/analytics.xlsx`}
           target="_blank" rel="noopener noreferrer" data-testid="btn-export-xlsx">
          <Button variant="outline" className="rounded-full">
            <DownloadSimple size={16} className="mr-2" /> {t("panafrican.export_xlsx")}
          </Button>
        </a>
      </div>

      {/* Global metrics band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard
          icon={Buildings} label={t("panafrican.metric_institutions")}
          value={tot.schools} accent="primary" testid="metric-schools"
        />
        <MetricCard
          icon={GraduationCap} label={t("panafrican.metric_students")}
          value={tot.students} accent="accent" testid="metric-students"
          growth={<Growth current={tot.students} previous={tot.prev_students} isFr={isFr} />}
          growthLabel={t("panafrican.vs_prev_snapshot")}
        />
        <MetricCard
          icon={CurrencyDollar} label={t("panafrican.metric_revenue")}
          value={`${Math.round(monthRev/1000)}k`} suffix="F CFA" accent="secondary" testid="metric-revenue"
          growth={<Growth current={monthRev} previous={prevMonthRev} isFr={isFr} />}
          growthLabel={t("panafrican.vs_prev_month")}
        />
        <MetricCard
          icon={Warning} label={t("panafrican.metric_unpaid")}
          value={`${Math.round((tot.unpaid_balance_all||0)/1000)}k`} suffix="F CFA" accent="destructive" testid="metric-unpaid"
        />
      </div>

      {/* Map + hovered country panel */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-3 rounded-2xl border border-border bg-card p-4 sm:p-6 relative overflow-hidden" data-testid="africa-map">
          <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
            {t("panafrican.institutions_map")}
          </div>
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-h-[560px]" role="img" aria-label="Africa map">
            <path d={CONTINENT_PATH}
                  fill="hsl(var(--muted))" stroke="hsl(var(--border))"
                  strokeWidth="1.5" fillOpacity="0.9" />
            {countryList.map(c => {
              const meta = COUNTRY_META[c.cc];
              if (!meta) return null;
              const [x, y] = project(meta.lat, meta.lng);
              const r = 6 + Math.round((c.students / maxStudents) * 22);
              const active = hoverCC === c.cc;
              const isFiltered = filterCC === c.cc;
              const dim = filterCC && filterCC !== c.cc;
              return (
                <g key={c.cc}
                   onMouseEnter={() => setHoverCC(c.cc)}
                   onMouseLeave={() => setHoverCC(null)}
                   onClick={() => setFilterCC(filterCC === c.cc ? null : c.cc)}
                   style={{ cursor: "pointer", opacity: dim ? 0.35 : 1, transition: "opacity 200ms" }}
                   data-testid={`map-marker-${c.cc}`}
                >
                  <circle cx={x} cy={y} r={r + 6} fill="hsl(var(--accent))" fillOpacity={active || isFiltered ? 0.28 : 0.12}>
                    {!active && !isFiltered && (
                      <animate attributeName="r" values={`${r+2};${r+10};${r+2}`} dur="2.5s" repeatCount="indefinite" />
                    )}
                  </circle>
                  <circle cx={x} cy={y} r={r}
                          fill={isFiltered ? "hsl(var(--primary))" : "hsl(var(--accent))"}
                          stroke="white" strokeWidth={isFiltered ? 3 : 2} />
                  <text x={x} y={y - r - 6} textAnchor="middle" className="font-semibold"
                        fontSize="11" fill="hsl(var(--foreground))">
                    {meta.flag} {c.students}
                  </text>
                </g>
              );
            })}
          </svg>
          <div className="text-[10px] text-muted-foreground/70 mt-1">{t("panafrican.map_legend")}</div>
        </div>

        <div className="lg:col-span-2 space-y-3">
          {filteredCountries.map(c => {
            const meta = COUNTRY_META[c.cc] || { flag: "🌍", name: c.cc, capital: "" };
            const active = hoverCC === c.cc || filterCC === c.cc;
            const pct = (c.students / (tot.students || 1)) * 100;
            return (
              <div
                key={c.cc}
                onMouseEnter={() => setHoverCC(c.cc)}
                onMouseLeave={() => setHoverCC(null)}
                onClick={() => setFilterCC(filterCC === c.cc ? null : c.cc)}
                className={`rounded-xl border p-4 transition-all cursor-pointer ${active ? "border-accent bg-accent/5" : "border-border bg-card hover:border-accent/40"}`}
                data-testid={`country-card-${c.cc}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl leading-none">{meta.flag}</span>
                      <div>
                        <div className="font-heading font-bold text-base">{meta.name}</div>
                        <div className="text-[11px] text-muted-foreground">
                          <MapPin size={10} weight="duotone" className="inline mr-0.5" />{meta.capital} · {c.schools.length} {t("panafrican.card_inst")}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold text-lg text-primary">{c.students}</div>
                    <div className="text-[9px] uppercase tracking-widest text-muted-foreground">{t("panafrican.card_students")}</div>
                  </div>
                </div>
                <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full bg-accent transition-all" style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                  <div>
                    <div className="text-muted-foreground">{t("panafrican.card_teachers")}</div>
                    <div className="font-semibold">{c.teachers}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">{t("panafrican.card_revenue")}</div>
                    <div className="font-semibold text-secondary flex items-center gap-1">
                      {fmtCurrency(c.month_revenue, c.currency)}
                      <Growth current={c.month_revenue} previous={c.prev_month_revenue} isFr={isFr} />
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">{t("panafrican.card_unpaid")}</div>
                    <div className={`font-semibold ${c.unpaid_balance > 0 ? "text-destructive" : ""}`}>{fmtCurrency(c.unpaid_balance, c.currency)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Schools table */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="schools-table">
        <div className="p-4 sm:p-5 border-b border-border flex items-center gap-2">
          <TrendUp size={18} weight="duotone" className="text-accent" />
          <div className="font-heading font-semibold">{t("panafrican.table_ranking")}</div>
          {filterCC && (
            <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-accent/10 text-accent">
              {filterMeta?.flag} {filterMeta?.name}
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[11px] uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2">#</th>
                <th className="text-left px-4 py-2">{t("panafrican.col_institution")}</th>
                <th className="text-left px-4 py-2">{t("panafrican.col_country")}</th>
                <th className="text-right px-4 py-2">{t("panafrican.metric_students")}</th>
                <th className="text-right px-4 py-2 hidden md:table-cell">{t("panafrican.col_teachers")}</th>
                <th className="text-right px-4 py-2 hidden md:table-cell">{t("panafrican.col_classes")}</th>
                <th className="text-right px-4 py-2">{t("panafrican.col_month_rev")}</th>
                <th className="text-right px-4 py-2">{t("panafrican.col_unpaid")}</th>
              </tr>
            </thead>
            <tbody>
              {filteredSchools.map((s, i) => {
                const meta = COUNTRY_META[s.country] || { flag: "🌍" };
                return (
                  <tr key={s.school_id} className="border-t border-border hover:bg-muted/20 transition-colors cursor-pointer"
                      onClick={() => setFilterCC(s.country)}
                      data-testid={`row-school-${s.school_id}`}>
                    <td className="px-4 py-2.5 text-muted-foreground">{i + 1}</td>
                    <td className="px-4 py-2.5 font-semibold">
                      <span className="inline-block w-1.5 h-4 rounded-sm align-middle mr-2" style={{ background: s.primary_color || "#1a2a6c" }} />
                      {s.school_name}
                    </td>
                    <td className="px-4 py-2.5"><span className="mr-1">{meta.flag}</span>{s.country}</td>
                    <td className="px-4 py-2.5 text-right font-mono">{s.students}</td>
                    <td className="px-4 py-2.5 text-right font-mono hidden md:table-cell">{s.teachers}</td>
                    <td className="px-4 py-2.5 text-right font-mono hidden md:table-cell">{s.classes}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-secondary">
                      <span className="inline-flex items-center gap-1">
                        {fmtCurrency(s.month_revenue, s.currency)}
                        <Growth current={s.month_revenue} previous={s.prev_month_revenue} isFr={isFr} />
                      </span>
                    </td>
                    <td className={`px-4 py-2.5 text-right font-mono ${s.unpaid_balance > 0 ? "text-destructive" : ""}`}>
                      {fmtCurrency(s.unpaid_balance, s.currency)}
                    </td>
                  </tr>
                );
              })}
              {filteredSchools.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-sm text-muted-foreground">—</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, suffix, accent, testid, growth, growthLabel }) {
  const bg = { primary: "bg-primary/5 text-primary", accent: "bg-accent/10 text-accent",
               secondary: "bg-secondary/10 text-secondary", destructive: "bg-destructive/10 text-destructive" }[accent] || "";
  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
        <Icon size={18} weight="duotone" />
      </div>
      <div className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">
        {value}{suffix ? <span className="text-sm text-muted-foreground font-normal ml-1">{suffix}</span> : null}
      </div>
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
        {growth && (
          <div className="flex items-center gap-1" title={growthLabel}>
            {growth}
          </div>
        )}
      </div>
    </div>
  );
}
